import DataSource from "devextreme/data/data_source";
import { useMemo } from "react";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import type { AnyObject } from "@entrilia/shared/types.ts";
import type {
  ChartRowResponse,
  ChartStyleSettings,
  DimensionField,
  PreviewMode,
  ReportField,
  ReportType,
  ValueCell,
} from "../../../../../../../api/types/svTypes.ts";
import BiErrorCodeComponent from "../common/bi-error/BiErrorCodeComponent.tsx";
import PreviewComponentAlert from "../common/PreviewComponentAlert.tsx";
import ValidationState from "../common/ValidationState.tsx";
import Chart from "./Chart.tsx";
import type { ExportChartFormats } from "./ChartProps.ts";
import type { ChartGridState } from "./hooks/gridState.types.ts";

interface Props {
  state: ChartGridState;
  argumentFields: DimensionField[];
  values: ReportField[];
  reportType: ReportType | undefined;
  settings: ChartStyleSettings;
  previewMode: PreviewMode;
  exportFuncCallback?: (callback: ((fileName: string, format: ExportChartFormats) => void) | undefined) => void;
  onDrillDown?: (field: ReportField, value: ValueCell) => void;
}
export default function PreviewComponent({
  state,
  argumentFields,
  values,
  reportType,
  settings,
  exportFuncCallback,
  onDrillDown,
  previewMode,
}: Props) {
  const valueFieldKeys = useMemo(() => new Set(values.map((value) => value.config.guid)), [values]);

  const dataSource = useMemo(() => {
    return new DataSource({
      paginate: false,
      store: state.data,
      map: (row: ChartRowResponse) => {
        const result: AnyObject = { rowId: row.rowId };
        Object.keys(row).forEach((key) => {
          if (key === "rowId") {
            return;
          }
          const cell = row[key];
          if (valueFieldKeys.has(key)) {
            // Measure fields must stay numeric; a string fallback makes the field type-inconsistent (DevExpress E2004).
            result[key] = typeof cell?.value === "number" ? cell.value : null;
          } else if (cell?.value) {
            result[key] = cell.value;
          } else {
            result[key] = "No Value";
          }
        });

        return result;
      },
    });
  }, [state.data, valueFieldKeys]);

  return (
    <BiErrorCodeComponent errorMessage={state.error}>
      <ValidationState state={state.configurationValidity} fields={values}>
        <PreviewComponentAlert error={state.error} failedMeasures={state.failedMeasures} />
        {!state.error &&
          !state.calculating &&
          state.fieldsConfigured &&
          state.configurationValidity.valid &&
          reportType !== undefined && (
            <Chart
              type={reportType}
              data={state.data || []}
              dataSource={dataSource}
              args={argumentFields}
              values={values}
              settings={settings}
              exportFunc={(callback) => exportFuncCallback?.(callback)}
              onDrillDown={onDrillDown}
              previewMode={previewMode}
            />
          )}
      </ValidationState>
      {state.calculating && <InlineLoader />}
    </BiErrorCodeComponent>
  );
}
